import { type Pinia } from "pinia";
import { onMounted, ref } from "vue";

import { useLogger } from "@vm/logger";

import { useComparisonConfigStore } from "@/config/config-store";
import runtimeConfig from "@/config/runtime.config";

import { ComparisonDataServiceTableau } from "@/comparison/comparison-data-service-tableau";

import { useComparisonStore } from "@/comparison/comparison-store";
import { useSettingsEditorStore } from "@/editor/use-settings-editor-store";

import { ComparisonDataServiceMock } from "@/comparison/comparison-data-service-mock";

export function useSetupApp(pinia: Pinia) {
  const initialized = ref(false);
  const isBusy = ref(false);
  const friendlyError = ref("");

  const { logger } = useLogger();
  const comparisonStore = useComparisonStore();
  const { initialize: initializeEditorStore } = useSettingsEditorStore(pinia);
  const { initialize: initializeConfigStore } = useComparisonConfigStore(pinia);

  async function initializeConfig() {
    try {
      if (runtimeConfig.enableTableau) {
        await initializeEditorStore();
      }
      await initializeConfigStore();
      return true;
    } catch (error) {
      friendlyError.value =
        "Er is een fout opgetreden bij het ophalen van de instellingen.";
      logger.error("Failed to initialize settings stores", error);
      return false;
    }
  }

  async function setup() {
    const configInitialized = await initializeConfig();
    if (!configInitialized) {
      return false;
    }

    const service = runtimeConfig.enableTableau
      ? new ComparisonDataServiceTableau()
      : new ComparisonDataServiceMock();

    comparisonStore.setDataService(service);

    try {
      await comparisonStore.initialize();
      return true;
    } catch (error) {
      friendlyError.value =
        "Er is een fout opgetreden bij het laden van de vergelijkingsgegevens.";
      logger.error("Failed to initialize comparison", error);
      return false;
    }
  }

  onMounted(async () => {
    isBusy.value = true;
    try {
      initialized.value = await setup();
    } catch (error) {
      logger.error("Error during app initialization", error);
      friendlyError.value = "Laden van data mislukt";
    } finally {
      isBusy.value = false;
    }
  });

  return {
    initialized,
    isBusy,
    friendlyError,
  };
}
